'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CreateCredentialDto,
  CredentialsApiDto,
} from '@terra/shared-contracts-applications';
import { useTranslations } from 'next-intl';
import { v4 as uuidv4 } from 'uuid';
import { toasterApi } from '../toast/ToastProvider';
import type { CredentialData } from './add-credentials-dialog/AddCredentialsDialogStore';
import type { DisableCredentialParams } from './credential-card/CredentialCard';
import {
  mapCredentialToDialogData,
  sanitizeCredential,
} from './credential-dialog-data';
import {
  collectTransitionedCompletions,
  pruneLiveStreamCredentialIds,
  pruneStreamingIds,
  pruneTestingCredentialIds,
  resolveVerificationStreamIdForDisplay,
  shouldCloseOpenStream,
  type VerificationStatusById,
  withoutKey,
  withoutLiveStreamCredentials,
} from './credential-verification-state';

type Credential = CredentialsApiDto['credentials'][number];

type VerificationResult = { success: boolean; error?: string | null };

type BulkVerificationResult = VerificationResult & {
  results?: Array<{
    credentialId: string;
    success: boolean;
    error?: string;
  }>;
};

export type CredentialListMutations = {
  saveCredential: (
    body: CreateCredentialDto,
  ) => Promise<{ credential: { id: string } }>;
  deleteCredential: (credentialId: string) => Promise<unknown>;
  toggleCredentialStatus: (params: DisableCredentialParams) => Promise<unknown>;
  verifyCredential: (params: {
    credentialId: string;
    streamingId: string;
  }) => Promise<VerificationResult>;
  verifyAllCredentials: (params: {
    streamingIds: Record<string, string>;
  }) => Promise<BulkVerificationResult>;
  abortCredentialVerification: (
    credentialId: string,
  ) => Promise<{ success: boolean }>;
};

export const useCredentialListController = ({
  credentials,
  mutations,
}: {
  credentials: Credential[] | undefined;
  mutations: CredentialListMutations;
}) => {
  const tCommon = useTranslations('designSystem.common');
  const tCredentials = useTranslations('designSystem.credentials');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] =
    useState<CredentialData | null>(null);
  const [deletingCredential, setDeletingCredential] =
    useState<Credential | null>(null);
  const [isDeletingCredential, setIsDeletingCredential] = useState(false);
  const [testingCredentialIds, setTestingCredentialIds] = useState<Set<string>>(
    new Set(),
  );
  const [streamingIdByCredentialId, setStreamingIdByCredentialId] = useState<
    Record<string, string>
  >({});
  const [liveStreamCredentialIds, setLiveStreamCredentialIds] = useState<
    Set<string>
  >(() => new Set());
  const [openStreamCredentialId, setOpenStreamCredentialId] = useState<
    string | null
  >(null);
  const [isTestingAll, setIsTestingAll] = useState(false);
  const previousStatusByIdRef = useRef<VerificationStatusById>({});
  const credentialsList = useMemo(() => credentials ?? [], [credentials]);

  const updateTestingCredentialIds = useCallback(
    (update: (previous: Set<string>) => Set<string>) => {
      setTestingCredentialIds((previous) => {
        const next = update(previous);

        if (next !== previous && next.size === 0) {
          setIsTestingAll(false);
        }

        return next;
      });
    },
    [],
  );

  const removeCredentialsFromTestingState = useCallback(
    (credentialIds: readonly string[]) => {
      updateTestingCredentialIds(
        (previous) =>
          new Set(
            [...previous].filter(
              (credentialId) => !credentialIds.includes(credentialId),
            ),
          ),
      );
    },
    [updateTestingCredentialIds],
  );

  const clearCredentialVerificationState = useCallback(
    (credentialId: string) => {
      removeCredentialsFromTestingState([credentialId]);
      setStreamingIdByCredentialId((previous) =>
        withoutKey(previous, credentialId),
      );
      setLiveStreamCredentialIds((previous) => {
        if (!previous.has(credentialId)) {
          return previous;
        }

        const next = new Set(previous);

        next.delete(credentialId);

        return next;
      });
      setOpenStreamCredentialId((previous) =>
        previous === credentialId ? null : previous,
      );
    },
    [removeCredentialsFromTestingState],
  );

  const markVerificationStreamStarted = useCallback((credentialId: string) => {
    setLiveStreamCredentialIds((previous) => {
      if (previous.has(credentialId)) {
        return previous;
      }

      return new Set([...previous, credentialId]);
    });
  }, []);

  const getVerificationStreamingIdForDisplay = useCallback(
    (credentialId: string) =>
      resolveVerificationStreamIdForDisplay({
        streamingId: streamingIdByCredentialId[credentialId],
        hasReceivedBrowserFrame: liveStreamCredentialIds.has(credentialId),
      }),
    [liveStreamCredentialIds, streamingIdByCredentialId],
  );

  useEffect(() => {
    if (!credentials || credentials.length === 0) {
      return;
    }

    const { completedCredentialIds, statusById } =
      collectTransitionedCompletions({
        credentials,
        previousStatusById: previousStatusByIdRef.current,
      });

    previousStatusByIdRef.current = statusById;

    updateTestingCredentialIds((previous) =>
      pruneTestingCredentialIds({
        testingCredentialIds: previous,
        credentials,
        completedCredentialIds,
      }),
    );
    setStreamingIdByCredentialId((previous) =>
      pruneStreamingIds({
        streamingIdByCredentialId: previous,
        credentials,
        completedCredentialIds,
      }),
    );
    setLiveStreamCredentialIds((previous) =>
      pruneLiveStreamCredentialIds({
        liveStreamCredentialIds: previous,
        credentials,
        completedCredentialIds,
      }),
    );
    setOpenStreamCredentialId((previous) =>
      previous &&
      shouldCloseOpenStream({
        openStreamCredentialId: previous,
        credentials,
        completedCredentialIds,
      })
        ? null
        : previous,
    );
  }, [credentials, updateTestingCredentialIds]);

  const openCreateCredentialDialog = useCallback(() => {
    setEditingCredential(null);
    setIsDialogOpen(true);
  }, []);

  const handleEditCredential = useCallback((credential: Credential) => {
    setEditingCredential(mapCredentialToDialogData(credential));
    setIsDialogOpen(true);
  }, []);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingCredential(null);
    }
  }, []);

  const handleTestCredential = useCallback(
    async (credentialId: string) => {
      if (isTestingAll) {
        return;
      }

      const streamingId = uuidv4();

      setStreamingIdByCredentialId((previous) => ({
        ...previous,
        [credentialId]: streamingId,
      }));
      setLiveStreamCredentialIds((previous) =>
        withoutLiveStreamCredentials({
          liveStreamCredentialIds: previous,
          credentialIds: [credentialId],
        }),
      );
      setTestingCredentialIds(
        (previous) => new Set([...previous, credentialId]),
      );
      toasterApi.success({ title: tCredentials('test.starting') });

      try {
        const result = await mutations.verifyCredential({
          credentialId,
          streamingId,
        });

        if (!result.success) {
          toasterApi.error({
            title: tCredentials('test.failed'),
            description: result.error || undefined,
          });
          clearCredentialVerificationState(credentialId);
        }
      } catch (error) {
        console.error('Failed to test credential:', error);
        toasterApi.error({ title: tCredentials('test.error') });
        clearCredentialVerificationState(credentialId);
      }
    },
    [clearCredentialVerificationState, isTestingAll, mutations, tCredentials],
  );

  const handleAbortCredential = useCallback(
    async (credentialId: string) => {
      const result = await mutations.abortCredentialVerification(credentialId);

      if (result.success) {
        clearCredentialVerificationState(credentialId);
      }
    },
    [clearCredentialVerificationState, mutations],
  );

  const handleSaveCredential = useCallback(
    async (credentialData: CredentialData): Promise<void> => {
      const isCreatingCredential = !credentialData.id;

      try {
        const { credential } = await mutations.saveCredential(
          sanitizeCredential(credentialData),
        );

        setIsDialogOpen(false);
        setEditingCredential(null);

        if (isCreatingCredential) {
          void handleTestCredential(credential.id);
        }
      } catch (error) {
        console.error('Failed to save credential:', error);
        toasterApi.error({ title: tCredentials('saveError') });
      }
    },
    [handleTestCredential, mutations, tCredentials],
  );

  const handleDisableCredential = useCallback(
    async (params: DisableCredentialParams) => {
      try {
        await mutations.toggleCredentialStatus(params);
      } catch (error) {
        console.error('Failed to toggle credential status:', error);
        toasterApi.error({ title: tCommon('error') });
      }
    },
    [mutations, tCommon],
  );

  const handleTestAllCredentials = useCallback(async () => {
    const enabledCredentialIds = credentialsList
      .filter((credential) => !credential.isDisabled)
      .map((credential) => credential.id);

    if (enabledCredentialIds.length === 0) {
      return;
    }

    const streamingIds = Object.fromEntries(
      enabledCredentialIds.map((credentialId) => [credentialId, uuidv4()]),
    );

    setIsTestingAll(true);
    setTestingCredentialIds(
      (previous) => new Set([...previous, ...enabledCredentialIds]),
    );
    setStreamingIdByCredentialId((previous) => ({
      ...previous,
      ...streamingIds,
    }));
    setLiveStreamCredentialIds((previous) =>
      withoutLiveStreamCredentials({
        liveStreamCredentialIds: previous,
        credentialIds: enabledCredentialIds,
      }),
    );
    toasterApi.success({ title: tCredentials('test.starting') });

    try {
      const result = await mutations.verifyAllCredentials({ streamingIds });
      const failedCredentialIds = (result.results ?? [])
        .filter((entry) => !entry.success)
        .map((entry) => entry.credentialId);

      for (const credentialId of failedCredentialIds) {
        clearCredentialVerificationState(credentialId);
      }

      if (!result.success) {
        toasterApi.error({
          title: tCredentials('test.failed'),
          description: result.error || undefined,
        });
      }
    } catch (error) {
      console.error('Failed to test credentials:', error);
      toasterApi.error({ title: tCredentials('test.error') });
      setOpenStreamCredentialId(null);
      removeCredentialsFromTestingState(enabledCredentialIds);
      setStreamingIdByCredentialId((previous) => {
        const next = { ...previous };

        for (const credentialId of enabledCredentialIds) {
          delete next[credentialId];
        }

        return next;
      });
      setLiveStreamCredentialIds((previous) =>
        withoutLiveStreamCredentials({
          liveStreamCredentialIds: previous,
          credentialIds: enabledCredentialIds,
        }),
      );
    } finally {
      setIsTestingAll(false);
    }
  }, [
    clearCredentialVerificationState,
    credentialsList,
    mutations,
    removeCredentialsFromTestingState,
    tCredentials,
  ]);

  const requestDeleteCredential = useCallback((credential: Credential) => {
    setDeletingCredential(credential);
  }, []);

  const clearDeletingCredential = useCallback(() => {
    setDeletingCredential(null);
  }, []);

  const handleDeleteCredential = useCallback(async () => {
    if (!deletingCredential) {
      return;
    }

    setIsDeletingCredential(true);

    try {
      await mutations.deleteCredential(deletingCredential.id);
      setDeletingCredential(null);
    } catch (error) {
      console.error('Failed to delete credential:', error);
      toasterApi.error({ title: tCredentials('deleteError') });
    } finally {
      setIsDeletingCredential(false);
    }
  }, [deletingCredential, mutations, tCredentials]);

  return {
    credentialsList,
    isDialogOpen,
    editingCredential,
    deletingCredential,
    isDeletingCredential,
    streamingIdByCredentialId,
    getVerificationStreamingIdForDisplay,
    markVerificationStreamStarted,
    openStreamCredentialId,
    setOpenStreamCredentialId,
    openVerificationStream: (credentialId: string) => {
      if (!liveStreamCredentialIds.has(credentialId)) {
        return;
      }

      setOpenStreamCredentialId(credentialId);
    },
    testingCredentialIds,
    openCreateCredentialDialog,
    handleEditCredential,
    handleDialogOpenChange,
    handleSaveCredential,
    handleTestCredential,
    handleAbortCredential,
    handleTestAllCredentials,
    isTestingAllCredentials: isTestingAll,
    handleDisableCredential,
    requestDeleteCredential,
    clearDeletingCredential,
    handleDeleteCredential,
  };
};
